import { useMutation, useQueryClient } from '@tanstack/react-query';
import { httpService } from '@services/httpService';
import { ApiRoute } from '@routes/apiRoutes';
import { QueryKeys } from '@enums/queryKeys';
import { DrawRequestCreateResponse } from '@models/drawRequests/drawRequestCreateResponse';
import { DrawRequestDetailResponseModel } from '@models/drawRequests/drawRequestDetailResponseModel';
import { DrawRequestCreateRequestModelNew } from '@models/drawRequests/drawRequestCreateRequestModelNew';
import { DrawRequestIdModel } from '@models/drawRequests/drawRequestIdModel';
import { DrawRequestUpdateBudgetItemsRequestModel } from '@models/drawRequests/drawRequestUpdateBudgetItemsRequestModel';

export const useDrawRequestCommandApi = {
  useSubmitDrawRequest: () => {
    const queryClient = useQueryClient();

    const submitDrawRequest = async (
      request: DrawRequestCreateRequestModelNew
    ) => {
      return await httpService.post<DrawRequestCreateResponse>(
        ApiRoute.ConstructionDrawRequestCreate,
        request
      );
    };

    return useMutation({
      mutationFn: (request: DrawRequestCreateRequestModelNew) =>
        submitDrawRequest(request),
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: query =>
            Array.isArray(query.queryKey) &&
            [
              QueryKeys.DRAW_REQUESTS,
              QueryKeys.CONSTRUCTION_DRAW_REQUEST_FEATURE_ENABLED,
              QueryKeys.LOAN_PROGRESS,
              QueryKeys.BUDGET_ITEMS,
            ].includes(query.queryKey[0]),
        });
      },
    });
  },

  useDeleteDrawRequest: () => {
    const queryClient = useQueryClient();

    const deleteDrawRequest = async (request: DrawRequestIdModel) => {
      return await httpService.post<boolean>(
        ApiRoute.ConstructionDrawRequestDelete,
        request
      );
    };

    return useMutation({
      mutationFn: (request: DrawRequestIdModel) => deleteDrawRequest(request),
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: query =>
            Array.isArray(query.queryKey) &&
            [
              QueryKeys.DRAW_REQUESTS,
              QueryKeys.CONSTRUCTION_DRAW_REQUEST_FEATURE_ENABLED,
              QueryKeys.LOAN_PROGRESS,
              QueryKeys.BUDGET_ITEMS,
            ].includes(query.queryKey[0]),
        });
      },
    });
  },

  useCancelDrawRequest: () => {
    const queryClient = useQueryClient();

    const cancelDrawRequest = async (request: DrawRequestIdModel) => {
      return await httpService.post<boolean>(
        ApiRoute.ConstructionDrawRequestCancel,
        request
      );
    };

    return useMutation({
      mutationFn: (request: DrawRequestIdModel) => cancelDrawRequest(request),
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: query =>
            Array.isArray(query.queryKey) &&
            [
              QueryKeys.DRAW_REQUESTS,
              QueryKeys.CONSTRUCTION_DRAW_REQUEST_FEATURE_ENABLED,
              QueryKeys.LOAN_PROGRESS,
              QueryKeys.BUDGET_ITEMS,
            ].includes(query.queryKey[0]),
        });
      },
    });
  },

  useUpdateDrawRequestBudgetItems: () => {
    const queryClient = useQueryClient();

    const updateBudgetItems = async (
      request: DrawRequestUpdateBudgetItemsRequestModel
    ) => {
      return await httpService.post<DrawRequestDetailResponseModel[]>(
        ApiRoute.ConstructionDrawRequestUpdateBudgetedItems,
        request
      );
    };

    return useMutation({
      mutationFn: (request: DrawRequestUpdateBudgetItemsRequestModel) =>
        updateBudgetItems(request),
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: query =>
            Array.isArray(query.queryKey) &&
            [
              QueryKeys.DRAW_REQUESTS,
              QueryKeys.CONSTRUCTION_DRAW_REQUEST_FEATURE_ENABLED,
              QueryKeys.LOAN_PROGRESS,
              QueryKeys.DRAW_REQUEST_DETAILS,
              QueryKeys.BUDGET_ITEMS,
            ].includes(query.queryKey[0]),
        });
      },
    });
  },
};

export default useDrawRequestCommandApi;
